/**
 * ArrTooltipWrapper Component
 *
 * Renders either a Tooltip or a Dialog depending on the content length.
 * - If `children` length <= threshold → Tooltip
 * - If `children` length > threshold → Dialog
 *
 * Features:
 * - Supports custom tooltip content (string or ReactNode)
 * - "Learn more" link inside dialog footer
 * - Character counting via hidden DOM node
 * - Customizable placement and offset for Tooltip
 * - Preserves trigger's original `onClick` if provided
 */

import React, {useState, useEffect, useRef} from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Box,
  Typography,
  Grow,
  ClickAwayListener,
  DialogTitle,
  DialogActions,
  DialogProps,
} from '@mui/material';
import {CloseOutlined, OpenInNew} from '@mui/icons-material';
import {t} from 'i18next';
import './ArrTooltipWrapper.scss';
import '../../../styles/main.scss';
import {EXPLORE_LINK_URL, THRESHOLD} from '../../../shared/constants';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GAHelpAlertVariant,
  GAHelpInfoClickConfig,
  getHelpAlertSubPage,
  trackAuroraReviewCentralClick,
  trackHelpInfoTooltipClicked,
  trackHelpOrAlertView,
} from '../../../utils/analyticsUtils';
import {GA_SUBPAGE_TYPE} from '../../../constants/analyticsConstants';

export interface ArrTooltipWrapperProps {
  children: React.ReactNode /** Content that will be shown inside Tooltip or Dialog */;

  title?: React.ReactNode /** Optional title for the Dialog (string or ReactNode) */;

  threshold?: number /** Character threshold to decide between Tooltip or Dialog */;

  learnMoreUrl?: string /** Learn more URL (defaults to global constant) */;

  placement?: 'top' | 'bottom' | 'left' | 'right' /** Tooltip placement */;

  offset?: [number, number] /** Offset for Tooltip popper */;

  trigger: React.ReactElement<
    React.HTMLAttributes<HTMLElement>
  > /** The trigger element that opens Tooltip or Dialog */;

  dialogClassName?: string;
  forceInLineToolTip?: boolean;
  closeTooltipOnEscPress?: boolean;
  isVisibleOnFocus?: boolean;
  autoFocusCloseButton?: boolean;
  disablePortal?: boolean;
  triggerKeyDownOnTab?: boolean;
  dataTestId?: string;
  tooltipClassName?: string;
  gaConfig?: {
    variant?: GAHelpAlertVariant;
    details?: string;
    click?: GAHelpInfoClickConfig;
  };
}

type DialogCloseReason = 'backdropClick' | 'escapeKeyDown';

const ArrTooltipWrapper: React.FC<ArrTooltipWrapperProps> = ({
  children,
  title = t('common.tooltipPopover.defaultTitle'),
  threshold = THRESHOLD,
  learnMoreUrl = EXPLORE_LINK_URL,
  placement = 'top',
  offset = [0, 0],
  trigger,
  dialogClassName,
  forceInLineToolTip,
  closeTooltipOnEscPress,
  isVisibleOnFocus,
  autoFocusCloseButton,
  disablePortal = false,
  triggerKeyDownOnTab = false,
  dataTestId = 'arr-tooltip-wrapper-div-btn',
  tooltipClassName,
  gaConfig,
}) => {
  const {trackGAEvent} = useAnalytics();
  const [open, setOpen] =
    useState(false); /** State to track whether Tooltip/Dialog is open */

  const [charCount, setCharCount] =
    useState<number>(0); /** State to store character length of children */

  const contentMeasureRef =
    useRef<HTMLDivElement>(
      null
    ); /** Ref for measuring text length of children */
  const ignoreNextFocusRef = useRef(false);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (closeBtnRef.current && autoFocusCloseButton && open) {
      timer = setTimeout(() => {
        closeBtnRef.current?.focus();
      }, 0);
    }

    return () => clearTimeout(timer);
  }, [autoFocusCloseButton, closeBtnRef, open]);

  /**
   * Effect:
   * To close the other tooltips if anyone is opened
   */
  useEffect(() => {
    const handler = () => setOpen(false);
    window.addEventListener('close-tooltips', handler);
    return () => window.removeEventListener('close-tooltips', handler);
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
      }
    };

    closeTooltipOnEscPress &&
      window.addEventListener('keydown', handleEsc, true);

    return () => {
      window.removeEventListener('keydown', handleEsc, true);
    };
  }, [open, closeTooltipOnEscPress]);

  /** Determines if content exceeds threshold */
  const isLongContent = charCount > threshold && !forceInLineToolTip;
  
  const helpAlertView = {
    variant: gaConfig?.variant,
    subpageType: isLongContent
      ? GA_SUBPAGE_TYPE.MODAL
      : GA_SUBPAGE_TYPE.TOOLTIP,
    title: typeof title === 'string' ? title : undefined,
    details: gaConfig?.details,
  };
  const modalSubPage = getHelpAlertSubPage({
    ...helpAlertView,
    subpageType: GA_SUBPAGE_TYPE.MODAL,
  });

  const trackOpened = () => {
    if (!isLongContent && gaConfig?.click) {
      trackHelpInfoTooltipClicked(trackGAEvent, helpAlertView, gaConfig.click);
    }

    trackHelpOrAlertView(trackGAEvent, helpAlertView);
  };

  /**
   * handleToggle: toggle the open state value
   */
  const handleToggle = () => {
    if (!open) {
      window.dispatchEvent(new Event('close-tooltips')); // close others
      trackOpened();
    }
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleCloseModal: DialogProps['onClose'] = (
    event: React.SyntheticEvent,
    reason: DialogCloseReason
  ) => {
    if (reason && reason === 'backdropClick') {
      return; /** Prevent closing on backdrop click */
    }
    setOpen(false); /** Close Tooltip/Dialog */
  };

  /**
   * Effect:
   * Counts number of characters in children using hidden DOM element.
   * Uses `innerText` to get rendered text.
   */
  useEffect(() => {
    if (contentMeasureRef.current) {
      const text = contentMeasureRef.current.innerText || '';
      setCharCount(text.trim().length);
    }
  }, [children]);

  /** Tooltip content (used if not exceeding threshold) */
  const TooltipContent = (
    <Box
      p={1}
      ref={contentMeasureRef}
      className={`typo-text body4_size tooltip-content ${tooltipClassName}`}
    >
      {children}
    </Box>
  );

  return (
    <>
      {isLongContent ? (
        /** Render Dialog if content length exceeds threshold */
        <>
          {React.cloneElement(trigger, {
            onClick: (e) => {
              e.stopPropagation();
              trigger.props?.onClick?.(e); // preserve original trigger handler
              handleToggle();
            },
            onKeyDown: (e) => {
              if (e.key === 'Tab' && triggerKeyDownOnTab) {
                e.stopPropagation();
                trigger.props?.onKeyDown?.(e); // preserve original trigger handler
              }
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                trigger.props?.onKeyDown?.(e); // preserve original trigger handler
                handleToggle();
              }
            },
          })}
          <Dialog
            open={open}
            className={`tooltip-popup ${dialogClassName}`}
            data-testid="tooltip-popup-dialog"
            onClose={(e: any) => {
              e.stopPropagation();
              handleCloseModal(e, 'backdropClick');
            }}
            aria-labelledby="tooltip-dialog-title"
            aria-describedby="tooltip-dialog-description"
            maxWidth="sm"
            fullWidth
            disablePortal={disablePortal}
            slotProps={{
              paper: {
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                },
              },
            }}
          >
            {/* Dialog Title with optional custom title and close button */}
            <DialogTitle
              id="tooltip-dialog-title"
              data-testd="tooltip-dialog-title"
            >
              <div
                className="tooltip-popup-title-container"
                data-testid={`arr-tooltip-wrapper-div`}
              >
                {/* Need to change once ArrIconButton completed */}
                <IconButton
                  aria-label="Close Modal"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  ref={closeBtnRef}
                  data-testid="arr-tooltip-wrapper-close-modal"
                >
                  <CloseOutlined fontSize="large" />
                </IconButton>
                {typeof title === 'string' ? (
                  <Typography className="typo-text headline3_size" tabIndex={0}>
                    {title}
                  </Typography>
                ) : (
                  title
                )}
              </div>
            </DialogTitle>

            {/* Dialog Content area */}
            <DialogContent dividers>
              <Box
                id="tooltip-dialog-description"
                className="typo-text body2_size tooltip-content"
                data-testd="tooltip-dialog-description"
              >
                {children}
              </Box>
            </DialogContent>

            {/* Dialog Footer with "Learn more" link */}
            <DialogActions>
              <div
                className="footer-box"
                data-testid={`arr-tooltip-wrapper-footer-desc`}
              >
                <Typography
                  className="typo-text subhead4_size footer-link-content"
                  data-testid={`arr-tooltip-wrapper-footer-desc-text`}
                >
                  {t('common.tooltipPopover.footerDesc')}
                </Typography>
                {/* Need to change once ArrIconButton completed */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation(); // prevent duplicate trigger clicks
                    trackAuroraReviewCentralClick(trackGAEvent, {
                      sourceAction: gaConfig?.details,
                      subPage: modalSubPage,
                    });
                    window.open(learnMoreUrl, 'noopener,noreferrer');
                    handleToggle();
                  }}
                  aria-label="Info"
                  data-testid="arr-tooltip-wrapper-info"
                >
                  <OpenInNew />
                </IconButton>
              </div>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        /** Render Tooltip if content length is within threshold */
        <ClickAwayListener onClickAway={handleClose}>
          <Box display="inline">
            <Tooltip
              open={open}
              onClose={handleClose}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={TooltipContent}
              arrow
              placement={placement}
              slots={{transition: Grow}}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [offset[0], offset[1] + 6],
                      },
                    },
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'viewport',
                      },
                    },
                    {
                      name: 'flip',
                      options: {
                        fallbackPlacements: ['top', 'bottom', 'left', 'right'],
                      },
                    },
                  ],
                },
                tooltip: {
                  sx: {
                    margin: '0rem !important',
                    padding: '0rem',
                    minWidth: '3rem',
                    maxWidth: {
                      xs: '12.5rem', // small screens
                      sm: '15rem', // tablet
                      md: '17.5rem', // desktop
                    },
                  },
                },
              }}
            >
              {React.cloneElement(trigger, {
                onMouseDown: () => {
                  ignoreNextFocusRef.current = true;
                },
                onClick: (e) => {
                  trigger.props.onClick?.(e); // preserve original handler
                  e.stopPropagation();
                  handleToggle();
                },
                onKeyDown: (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    trigger.props?.onKeyDown?.(e); // preserve original trigger handler
                    handleToggle();
                  }
                },
                onFocus: (e) => {
                  e.stopPropagation();
                  if (ignoreNextFocusRef.current) {
                    // consumed focus caused by pointer click
                    ignoreNextFocusRef.current = false;
                  } else if (isVisibleOnFocus && !open) {
                    trackOpened();
                    setOpen(true);
                  }
                },
                onBlur: (e) => {
                  e.stopPropagation();
                  if (isVisibleOnFocus) {
                    setOpen(false);
                  }
                },
              })}
            </Tooltip>
          </Box>
        </ClickAwayListener>
      )}
      {/* Hidden element for character length measuring */}
      <div
        ref={contentMeasureRef}
        className="hidden-measure"
        aria-hidden="true"
        data-testid={`arr-tooltip-wrapper-div-btn-${title}`}
      >
        {children}
      </div>
    </>
  );
};

export default ArrTooltipWrapper;
